import { useEffect, useMemo, useState } from "react";
import { useGetReviewData } from "../../../../api/ingredients";
import { useGetTransportTypesList } from "../../../../api/static-data";
import {
  ApiFarmGate,
  ApiProcessGate,
  ApiExternalTransportRecord,
  ExternalTransportRecord,
  InternalStorageRecord
} from "../../../../types/prodcut-data-review/product-data.types";
import { toExternalStorageRecord, toExternalTransportRecord } from "../utils/reviewData.utils";

interface UseReviewGateDataParams {
  drawingId: string | undefined;
}

/** Fetches the review payload, derives the external storage/transport view models, and
 * exposes the local-state mutators the gate cards and external tables save through. */
export const useReviewGateData = ({ drawingId }: UseReviewGateDataParams) => {
  const { data: reviewData, refetch: refetchReviewData } = useGetReviewData({ drawingId });

  // Same hook used by UpdateNodes transport components
  const { data: transportTypesList } = useGetTransportTypesList();
  const transportTypeMap = useMemo<Record<number, string>>(() => {
    if (!transportTypesList) return {};
    return Object.fromEntries(
      (transportTypesList as any[]).map((t: any) => [Number(t.key), t.text as string])
    );
  }, [transportTypesList]);

  const [farmGates, setFarmGates] = useState<ApiFarmGate[]>([]);
  const [processGates, setProcessGates] = useState<ApiProcessGate[]>([]);
  const [externalStorages, setExternalStorages] = useState<InternalStorageRecord[]>([]);
  const [externalTransports, setExternalTransports] = useState<ExternalTransportRecord[]>([]);

  useEffect(() => {
    if (reviewData) {
      setFarmGates(reviewData.farmGate);
      setProcessGates(reviewData.processGate);
      setExternalStorages((reviewData.externalStorage ?? []).map(toExternalStorageRecord));
      setExternalTransports(
        (reviewData.externalTransportation ?? []).map((api: ApiExternalTransportRecord) =>
          toExternalTransportRecord(api, transportTypeMap)
        )
      );
    }
  }, [reviewData, transportTypeMap]);

  const handleGateChange = (id: string, next: ApiFarmGate) =>
    setFarmGates(prev => prev.map(g => (g._id === id ? next : g)));

  const handleProcessGateChange = (id: string, next: ApiProcessGate) =>
    setProcessGates(prev => prev.map(g => (g._id === id ? next : g)));

  const saveExternalStorage = (id: string, patch: Partial<InternalStorageRecord>) =>
    setExternalStorages(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));

  const saveExternalTransport = (id: string, patch: Partial<ExternalTransportRecord>) =>
    setExternalTransports(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));

  return {
    farmGates,
    processGates,
    externalStorages,
    externalTransports,
    transportTypeMap,
    refetchReviewData,
    handleGateChange,
    handleProcessGateChange,
    saveExternalStorage,
    saveExternalTransport
  };
};
